/**
 * Update Phonetic Narrations Script
 * 
 * This script updates all existing narrations in Firestore with properly
 * phonetized text using the generatePhoneticText function.
 * 
 * It processes all slide and dashboard narrations, preserving their existing
 * content while updating only the phoneticNarration field.
 */

import { db as presentationDb } from '../firebase/firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { generatePhoneticText, getNarrationText } from './unifiedNarrationSync';
import { SlideNarration, DashboardNarration } from '../types/narration';

/**
 * Updates all slide narrations with properly phonetized text
 */
async function updateSlideNarrations() {
  console.log('Updating slide narrations with phonetic text...');
  const slideNarrationsRef = collection(presentationDb, 'slideNarrations');
  const snapshot = await getDocs(slideNarrationsRef);
  
  let updatedCount = 0;
  let skippedCount = 0;
  let errorCount = 0;
  
  for (const docSnapshot of snapshot.docs) {
    try {
      const narrationData = docSnapshot.data();
      const narrationText = narrationData.narration || '';
      
      // Generate phonetic text using our enhanced function
      const phoneticText = generatePhoneticText(narrationText);
      
      // Only update if the phonetic text is different from the current value
      // or if there is no current phonetic narration
      if (!narrationData.phoneticNarration || narrationData.phoneticNarration !== phoneticText) {
        const narrationRef = doc(presentationDb, 'slideNarrations', docSnapshot.id);
        await updateDoc(narrationRef, {
          phoneticNarration: phoneticText,
          // Add a note in the metadata that this was updated by the script
          updatedSource: 'phonetic-update-script',
          updatedAt: new Date().toISOString()
        });
        updatedCount++;
        console.log(`Updated slide narration ${docSnapshot.id}`);
      } else {
        skippedCount++;
      }
    } catch (error) {
      console.error(`Error updating slide narration ${docSnapshot.id}:`, error);
      errorCount++;
    }
  }
  
  return { updatedCount, skippedCount, errorCount };
}

/**
 * Updates all dashboard narrations with properly phonetized text
 */
async function updateDashboardNarrations() {
  console.log('Updating dashboard narrations with phonetic text...');
  const dashboardNarrationsRef = collection(presentationDb, 'dashboardNarrations');
  const snapshot = await getDocs(dashboardNarrationsRef);
  
  let updatedCount = 0;
  let skippedCount = 0;
  let errorCount = 0;
  
  for (const docSnapshot of snapshot.docs) {
    try {
      const narrationData = docSnapshot.data();
      const narrationText = narrationData.narration || '';
      
      // Generate phonetic text using our enhanced function
      const phoneticText = generatePhoneticText(narrationText);
      
      // Only update if the phonetic text is different from the current value
      // or if there is no current phonetic narration
      if (!narrationData.phoneticNarration || narrationData.phoneticNarration !== phoneticText) {
        const narrationRef = doc(presentationDb, 'dashboardNarrations', docSnapshot.id);
        await updateDoc(narrationRef, {
          phoneticNarration: phoneticText,
          // Add a note in the metadata that this was updated by the script
          updatedSource: 'phonetic-update-script',
          updatedAt: new Date().toISOString()
        });
        updatedCount++;
        console.log(`Updated dashboard narration ${docSnapshot.id}`);
      } else {
        skippedCount++;
      }
    } catch (error) {
      console.error(`Error updating dashboard narration ${docSnapshot.id}:`, error);
      errorCount++;
    }
  }
  
  return { updatedCount, skippedCount, errorCount };
}

/**
 * Main function to update all narrations
 */
export async function updateAllPhoneticNarrations() {
  console.log('Starting phonetic narration update process...');
  
  try {
    // Update slide narrations
    const slideResults = await updateSlideNarrations();
    console.log('Slide narration update complete:', slideResults);
    
    // Update dashboard narrations
    const dashboardResults = await updateDashboardNarrations();
    console.log('Dashboard narration update complete:', dashboardResults);
    
    // Return combined results
    return {
      success: true,
      slideNarrations: slideResults,
      dashboardNarrations: dashboardResults,
      totalUpdated: slideResults.updatedCount + dashboardResults.updatedCount,
      totalSkipped: slideResults.skippedCount + dashboardResults.skippedCount,
      totalErrors: slideResults.errorCount + dashboardResults.errorCount
    };
  } catch (error) {
    console.error('Error updating phonetic narrations:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
}
